import React, { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Link,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';

import { Order } from '../../interfaces/services/orders';
import { formatCurrency, formatNumber, truncateId } from '../../utils/formattingUtils';
import { buildShipperLink } from '../../utils/navigationHelpers';
import { useFilters } from '../Filters/useFilters';

export const NUMBER_TOP_PERFORMERS = 5;

const TopShippersTable = ({
    loadingTopShippers,
    orders,
}: {
    loadingTopShippers: boolean;
    orders: Order[];
}): React.JSX.Element => {
    const filters = useFilters();

    const renderTopShippers = (): ReactNode => {
        if (loadingTopShippers) {
            return Array.from(new Array(NUMBER_TOP_PERFORMERS)).map((value, index) => {
                return (
                    <TableRow key={index}>
                        <TableCell colSpan={2}><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                        <TableCell><Skeleton /></TableCell>
                    </TableRow>
                );
            });
        }

        if (orders.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={4}>No orders found</TableCell>
                </TableRow>
            );
        }

        const shippers = orders.reduce((shipperSummary: Record<
            string,
            {
                orderVolume: number;
                linehaulRevenue: number;
                shipperName: string;
                id: string;
            }
        >, order): Record<
            string,
            {
                orderVolume: number;
                linehaulRevenue: number;
                shipperName: string;
                id: string;
            }
        > => {
            if (shipperSummary[order.pickup_party_id]) {
                shipperSummary[order.pickup_party_id].orderVolume += 1;
                shipperSummary[order.pickup_party_id].linehaulRevenue += order.linehaul_revenue_usd_amount;
            } else {
                shipperSummary[order.pickup_party_id] = {
                    orderVolume: 1,
                    linehaulRevenue: order.linehaul_revenue_usd_amount,
                    shipperName: order.pickup_party_name,
                    id: order.pickup_party_id,
                };
            }
            return shipperSummary;
        }, {});

        if (Object.keys(shippers).length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={4}>No shippers found</TableCell>
                </TableRow>
            );
        }

        const topShippers = Object.keys(shippers)
            .map((id) => { return shippers[id]; })
            .sort((a, b) => { return b.orderVolume - a.orderVolume; })
            .slice(0, NUMBER_TOP_PERFORMERS);

        return topShippers.map((shipper, index) => {
            return (
                <TableRow key={`top-shippers-row-${index}`}>
                    <TableCell colSpan={2}>
                        <Tooltip title={shipper.shipperName}>
                            <Link
                                component={RouterLink}
                                to={buildShipperLink(shipper.id, filters)}
                            >
                                {truncateId(shipper.shipperName)}
                            </Link>
                        </Tooltip>
                    </TableCell>
                    <TableCell>{formatNumber(shipper.orderVolume, 0, 0)}</TableCell>
                    <TableCell>
                        {formatCurrency(shipper.linehaulRevenue)}
                    </TableCell>
                </TableRow>
            );
        });
    };

    return (
        <TableContainer>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell colSpan={2}>Shipper</TableCell>
                        <TableCell>Orders</TableCell>
                        <TableCell>Revenue</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody style={{ overflow: 'auto' }}>
                    {renderTopShippers()}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default TopShippersTable;
